import { initReactI18next } from 'react-i18next';
import { SUPPORTED_LOCALES_ARR, type SupportedLocale } from '@zep-us/i18n';
import i18next, { InitOptions } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend, { ChainedBackendOptions } from 'i18next-chained-backend';
import { HttpBackendOptions } from 'i18next-http-backend';
import { enableStaticRendering } from 'mobx-react-lite';
import qs from 'qs';

import { registerEquipmentCatalog } from '@zep/game/registry';
import { getDefaultI18nOptions } from '@zep/i18n';
import { ignoreConsoleError, isClient, isServer, logVersion } from '@zep/utils';

enableStaticRendering(isServer());

// Plan D·16 — bundled equipmentItems.json is a dev-only warm-start. Prod builds
// rely entirely on `EquipmentCatalogBootstrapper` reading the API; the dynamic
// import below is DCE'd under `NODE_ENV='production'` so the JSON ships zero
// bytes to end users. In dev (and tests), it gives the UI something to render
// while the catalog API is still in flight.
if (process.env.NODE_ENV !== 'production') {
  void import('@zep/play/equipment').then(({ EQUIPMENT_ITEMS }) => {
    registerEquipmentCatalog(EQUIPMENT_ITEMS, { basePath: '/play' });
  });
}

if (isClient()) {
  // React의 문제로 불필요하게 Noisy한 Error가 발생하는데, 18.2.0이하로 downgrade하거나 19로 upgrade해야 고쳐집니다.
  // 개발 환경에서 직접 해당 warning을 무시하는 방식으로 해결합니다.
  // Ref: https://github.com/facebook/react/issues/28948
  ignoreConsoleError('React does not recognize the `fetchPriority` prop');
  ignoreConsoleError(
    'Invalid prop `data-sentry-element` supplied to `React.Fragment`',
  );

  logVersion();
}

if (isClient()) {
  const localeFromUrl = getLocaleFromUrl(window.location.search);

  const i18nConfig: InitOptions<ChainedBackendOptions & HttpBackendOptions> = {
    ...getDefaultI18nOptions({
      appName: 'play',
      namespaces: ['common', 'play', 'dashboard', 'wearables'],
      lng: localeFromUrl,
      supportedLngs: SUPPORTED_LOCALES_ARR,
    }),
  };

  i18next
    .use(LanguageDetector)
    .use(ChainedBackend)
    .use(initReactI18next)
    .init<ChainedBackendOptions>(i18nConfig)
    .catch(error => {
      console.error(error);
    });
}

function getLocaleFromUrl(queryString: string): SupportedLocale | undefined {
  let locale: SupportedLocale | undefined = undefined;

  try {
    const parsedQuery = qs.parse(queryString, { ignoreQueryPrefix: true });
    if (typeof parsedQuery.customData !== 'string') {
      return locale;
    }

    const customData = JSON.parse(
      decodeURIComponent(parsedQuery.customData),
    ) as { locale?: string };

    if (
      customData.locale &&
      SUPPORTED_LOCALES_ARR.includes(customData.locale as SupportedLocale)
    ) {
      locale = customData.locale as SupportedLocale;
    }
  } catch (error) {
    console.error('Error parsing customData:', error);
    return undefined;
  }

  return locale;
}
